import { useCallback, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { searchFlights } from 'api/flights'
import type { MappedFlightSearchResult } from 'api/flights'
import type { FlightSearchParams } from 'hooks/useSearchFlights'
import { FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES } from 'constants/flight'
import type { TransportOption } from 'tripPlanner/api/types'
import { parseSearchParams } from './flightShortlistUtils'

type RefreshStatus = 'idle' | 'refreshing' | 'success' | 'error'

export interface RefreshedFlight {
  option: TransportOption
  matchedJourney?: App.JourneyV2
  matchedDepartingJourney?: App.JourneyV2
  matchedReturningJourney?: App.JourneyV2
  newPrice?: number
  newExpiry?: number
  isSoldOut: boolean
}

interface UseRefreshResult {
  status: RefreshStatus
  refreshedFlights: Array<RefreshedFlight>
  refresh: (options: Array<TransportOption>) => Promise<Array<RefreshedFlight>>
}

function useRefreshShortlistedFlights(): UseRefreshResult {
  const [status, setStatus] = useState<RefreshStatus>('idle')
  const [refreshedFlights, setRefreshedFlights] = useState<Array<RefreshedFlight>>([])

  const currency = useAppSelector(state => state.geo.currentCurrency)
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const email = useAppSelector(state => state.auth.account.email)

  const refresh = useCallback(async(options: Array<TransportOption>): Promise<Array<RefreshedFlight>> => {
    setStatus('refreshing')
    setRefreshedFlights([])

    const paramGroups = new Map<string, { params: FlightSearchParams; options: Array<TransportOption> }>()

    for (const option of options) {
      const data = option.savedItemData
      if (data?.source !== 'le_flight') continue

      const parsed = parseSearchParams(data.searchParams)
      if (!parsed) continue

      const key = JSON.stringify(parsed)
      const existing = paramGroups.get(key)
      if (existing) {
        existing.options.push(option)
      } else {
        paramGroups.set(key, { params: parsed, options: [option] })
      }
    }

    try {
      const results: Array<RefreshedFlight> = []

      for (const [, group] of paramGroups) {
        let searchResult: MappedFlightSearchResult
        try {
          searchResult = await searchFlights({
            itinerary: group.params.itinerary,
            currency,
            occupancies: group.params.occupancies,
            fareClass: group.params.fareClass,
            region,
            fareType: group.params.fareType,
            email,
          }, { enableSabreContent: true })
        } catch {
          for (const option of group.options) {
            results.push({ option, isSoldOut: false })
          }
          continue
        }

        const allFares = searchResult.onewayFares.flat()
        const departingFares = searchResult.onewayFares[0] ?? []
        const returningFares = searchResult.onewayFares[1] ?? []
        const newExpiry = Date.now() + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000

        for (const option of group.options) {
          const data = option.savedItemData
          if (data?.source !== 'le_flight') continue

          const returnLegData = option.returnLeg?.savedItemData?.source === 'le_flight'
            ? option.returnLeg.savedItemData
            : undefined
          const isReturn = Boolean(option.returnLeg && returnLegData)

          const matchedDepartingJourney = data.journeyKey
            ? departingFares.find(j => j.bookingInfo.journeyKey === data.journeyKey)
            ?? allFares.find(j => j.bookingInfo.journeyKey === data.journeyKey)
            : undefined
          const matchedReturningJourney = returnLegData?.journeyKey
            ? returningFares.find(j => j.bookingInfo.journeyKey === returnLegData.journeyKey)
            ?? allFares.find(j => j.bookingInfo.journeyKey === returnLegData.journeyKey)
            : undefined

          if (matchedDepartingJourney && (!isReturn || matchedReturningJourney)) {
            results.push({
              option,
              matchedJourney: matchedDepartingJourney,
              matchedDepartingJourney,
              matchedReturningJourney,
              newPrice: isReturn
                ? undefined
                : matchedDepartingJourney.price.adult.totalFare,
              newExpiry,
              isSoldOut: false,
            })
          } else {
            results.push({ option, isSoldOut: true })
          }
        }
      }

      setRefreshedFlights(results)
      setStatus('success')
      return results
    } catch {
      setStatus('error')
      return []
    }
  }, [currency, region, email])

  return { status, refreshedFlights, refresh }
}

export default useRefreshShortlistedFlights
